import React, { Component } from "react";
import { connect } from "react-redux";
import { pmaActions } from "_store/_actions";
import DashboardBodyRange from "./DashboardBodyRange";
import DashboardBodyRangeEditorModal, {
  MAX_RANGE_FIELD_LENGTH,
} from "./DashboardBodyRangeEditorModal";
import {
  applyDisplayOverridesToRange,
  applyEditorStateToDtoRangeItem,
  buildRangeEditorState,
  buildRangesFromPriceRangeDto,
  clonePriceRangeList,
  getDtoOriginalDisplayValue,
  getPriceRangeListFromDto,
  getPriceRangeListKey,
  getRangeDisplayFieldDefs,
  isRangeDisplayEdited,
  syncVisibilityWithDisplayValues,
} from "./rangeDisplayUtils";

class DashboardBody extends Component {
  state = {
    rangesDraft: null,
    dtoDraft: null,
    modalOpen: false,
    editingSourceIndex: null,
    editFormData: null,
    editFormBaseline: null,
    saving: false,
    saveError: null,
    saveSuccess: null,
  };

  saveSuccessTimeoutId = null;

  componentDidMount() {
    this.syncRangesDraftFromProps();
  }

  componentWillUnmount() {
    if (this.saveSuccessTimeoutId) {
      clearTimeout(this.saveSuccessTimeoutId);
      this.saveSuccessTimeoutId = null;
    }
  }

  componentDidUpdate(prevProps) {
    const prevDto = prevProps.priceRangeDashBoardDetailsDTO;
    const nextDto = this.props.priceRangeDashBoardDetailsDTO;
    const prevRanges = prevProps.MQCityDetails?.dashBoardDetailsWithPriceRange;
    const nextRanges = this.props.MQCityDetails?.dashBoardDetailsWithPriceRange;
    if (
      prevDto !== nextDto ||
      prevRanges !== nextRanges ||
      prevProps.useCity !== this.props.useCity
    ) {
      this.syncRangesDraftFromProps();
    }
  }

  syncRangesDraftFromProps = () => {
    const { MQCityDetails, priceRangeDashBoardDetailsDTO, useCity } =
      this.props;
    const listKey = getPriceRangeListKey(Boolean(useCity));
    const dtoRanges = buildRangesFromPriceRangeDto(
      priceRangeDashBoardDetailsDTO,
      Boolean(useCity),
    );

    if (dtoRanges) {
      const sourceList = getPriceRangeListFromDto(
        priceRangeDashBoardDetailsDTO,
        Boolean(useCity),
      );
      this.setState({
        rangesDraft: dtoRanges,
        dtoDraft: {
          ...priceRangeDashBoardDetailsDTO,
          [listKey]: clonePriceRangeList(sourceList),
        },
      });
      return;
    }

    // Keep the in-progress/local draft if Redux temporarily has an incomplete DTO
    // after save (e.g. API returns a success body without the area/city list).
    const draftList = getPriceRangeListFromDto(
      this.state.dtoDraft,
      Boolean(useCity),
    );
    if (draftList?.length && this.state.rangesDraft?.length) {
      return;
    }

    const ranges = MQCityDetails?.dashBoardDetailsWithPriceRange;
    this.setState({
      rangesDraft: Array.isArray(ranges)
        ? ranges.map((range) => ({ ...range }))
        : null,
      dtoDraft: null,
    });
  };

  openEditModal = (sourceIndex, rangeNum) => {
    const { rangesDraft, dtoDraft } = this.state;
    const { agentData, currentArea, useCity } = this.props;
    const range = rangesDraft?.[sourceIndex];
    if (!range) {
      return;
    }

    const editFormData = buildRangeEditorState(range, {
      agentData,
      currentArea,
      rangeNum,
    });

    // Always seed originalValues from the DTO's originalDisplay* fields so
    // "Revert to Original" restores the server originals into the inputs.
    const dtoList = getPriceRangeListFromDto(dtoDraft, Boolean(useCity));
    const dtoItem = dtoList?.[sourceIndex];
    if (dtoItem && editFormData) {
      const originalValues = { ...(editFormData.originalValues || {}) };
      getRangeDisplayFieldDefs().forEach(({ name }) => {
        const fromDto = getDtoOriginalDisplayValue(dtoItem, name);
        if (fromDto != null) {
          originalValues[name] = fromDto;
        }
      });
      editFormData.originalValues = originalValues;
    }

    this.setState({
      modalOpen: true,
      editingSourceIndex: sourceIndex,
      editFormData,
      editFormBaseline: this.cloneEditFormSnapshot(editFormData),
      saveError: null,
      saveSuccess: null,
    });
  };

  cloneEditFormSnapshot = (editFormData) => {
    if (!editFormData) {
      return null;
    }
    return {
      displayOverrides: { ...(editFormData.displayOverrides || {}) },
      fieldVisibility: { ...(editFormData.fieldVisibility || {}) },
    };
  };

  isEditFormDirty = () => {
    const { editFormData, editFormBaseline } = this.state;
    if (!editFormData || !editFormBaseline) {
      return false;
    }

    const fields = getRangeDisplayFieldDefs();
    const currentDisplay = editFormData.displayOverrides || {};
    const baselineDisplay = editFormBaseline.displayOverrides || {};
    const currentVisibility = editFormData.fieldVisibility || {};
    const baselineVisibility = editFormBaseline.fieldVisibility || {};

    return fields.some(({ name }) => {
      const currentValue =
        currentDisplay[name] != null ? String(currentDisplay[name]) : "";
      const baselineValue =
        baselineDisplay[name] != null ? String(baselineDisplay[name]) : "";
      const currentShown = currentVisibility[name] !== false;
      const baselineShown = baselineVisibility[name] !== false;
      return currentValue !== baselineValue || currentShown !== baselineShown;
    });
  };

  closeEditModal = () => {
    this.setState({
      modalOpen: false,
      editingSourceIndex: null,
      editFormData: null,
      editFormBaseline: null,
    });
  };

  requestCloseEditModal = () => {
    if (this.state.saving || this.state.saveSuccess) {
      return;
    }
    if (this.isEditFormDirty()) {
      const discard = window.confirm(
        "You have unsaved changes. Discard them and close?",
      );
      if (!discard) {
        return;
      }
    }
    this.closeEditModal();
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      typeof value === "string" && value.length > MAX_RANGE_FIELD_LENGTH
        ? value.slice(0, MAX_RANGE_FIELD_LENGTH)
        : value;
    this.setState((prev) => ({
      editFormData: {
        ...prev.editFormData,
        displayOverrides: {
          ...prev.editFormData.displayOverrides,
          [name]: nextValue,
        },
      },
      saveError: null,
    }));
  };

  handleVisibilityChange = (e) => {
    const { name, checked } = e.target;
    this.setState((prev) => ({
      editFormData: {
        ...prev.editFormData,
        fieldVisibility: {
          ...prev.editFormData.fieldVisibility,
          [name]: checked,
        },
      },
      saveError: null,
    }));
  };

  resolveOriginalFieldValue = (name, editFormData, dtoItem) => {
    let originalValue = getDtoOriginalDisplayValue(dtoItem, name);
    if (originalValue == null) {
      originalValue = editFormData?.originalValues?.[name];
    }
    if (originalValue == null) {
      return null;
    }
    if (
      typeof originalValue === "string" &&
      originalValue.length > MAX_RANGE_FIELD_LENGTH
    ) {
      return originalValue.slice(0, MAX_RANGE_FIELD_LENGTH);
    }
    return originalValue;
  };

  handleRevertField = (name) => {
    this.setState((prev) => {
      const { editingSourceIndex, dtoDraft, editFormData } = prev;
      const dtoList = getPriceRangeListFromDto(
        dtoDraft,
        Boolean(this.props.useCity),
      );
      const dtoItem =
        editingSourceIndex != null ? dtoList?.[editingSourceIndex] : null;

      const nextValue = this.resolveOriginalFieldValue(
        name,
        editFormData,
        dtoItem,
      );
      if (nextValue == null) {
        return null;
      }

      return {
        editFormData: {
          ...editFormData,
          displayOverrides: {
            ...editFormData.displayOverrides,
            [name]: nextValue,
          },
          originalValues: {
            ...editFormData.originalValues,
            [name]: nextValue,
          },
        },
      };
    });
  };

  handleRevertAllFields = () => {
    this.setState((prev) => {
      const { editingSourceIndex, dtoDraft, editFormData } = prev;
      if (!editFormData?.displayOverrides) {
        return null;
      }

      const dtoList = getPriceRangeListFromDto(
        dtoDraft,
        Boolean(this.props.useCity),
      );
      const dtoItem =
        editingSourceIndex != null ? dtoList?.[editingSourceIndex] : null;

      const nextDisplay = { ...editFormData.displayOverrides };
      const nextOriginals = { ...(editFormData.originalValues || {}) };

      getRangeDisplayFieldDefs().forEach(({ name }) => {
        const nextValue = this.resolveOriginalFieldValue(
          name,
          editFormData,
          dtoItem,
        );
        if (nextValue == null) {
          return;
        }
        nextDisplay[name] = nextValue;
        nextOriginals[name] = nextValue;
      });

      return {
        editFormData: {
          ...editFormData,
          displayOverrides: nextDisplay,
          originalValues: nextOriginals,
        },
      };
    });
  };

  showSaveSuccess = (message = "Saved") => {
    if (this.saveSuccessTimeoutId) {
      clearTimeout(this.saveSuccessTimeoutId);
    }
    this.setState({ saveSuccess: message, saveError: null });
    this.saveSuccessTimeoutId = setTimeout(() => {
      this.setState({
        modalOpen: false,
        editingSourceIndex: null,
        editFormData: null,
        editFormBaseline: null,
        saveSuccess: null,
      });
      this.saveSuccessTimeoutId = null;
    }, 2000);
  };

  onEditFormSubmit = async () => {
    const { editFormData, editingSourceIndex, rangesDraft, dtoDraft } =
      this.state;
    const {
      agentData,
      currentArea,
      useCity,
      updateDashBoardDetailsWithPriceRange,
    } = this.props;
    const { listgenUserID } = agentData || {};
    const { mlsAreaID } = currentArea || {};

    if (
      editFormData == null ||
      editingSourceIndex == null ||
      !Array.isArray(rangesDraft) ||
      !this.isEditFormDirty() ||
      this.state.saving ||
      this.state.saveSuccess
    ) {
      return;
    }

    const previousRangesDraft = Array.isArray(rangesDraft)
      ? rangesDraft.map((range) => ({
          ...range,
          displayOverrides: range.displayOverrides
            ? { ...range.displayOverrides }
            : range.displayOverrides,
          originalValues: range.originalValues
            ? { ...range.originalValues }
            : range.originalValues,
          fieldVisibility: range.fieldVisibility
            ? { ...range.fieldVisibility }
            : range.fieldVisibility,
        }))
      : null;
    const listKey = getPriceRangeListKey(Boolean(useCity));
    const previousList = getPriceRangeListFromDto(dtoDraft, Boolean(useCity));
    const previousDtoDraft = dtoDraft
      ? {
          ...dtoDraft,
          [listKey]: clonePriceRangeList(previousList),
        }
      : null;

    const syncedVisibility = syncVisibilityWithDisplayValues(
      editFormData.displayOverrides,
      editFormData.fieldVisibility,
    );

    const nextRanges = rangesDraft.map((range, index) =>
      index === editingSourceIndex
        ? applyDisplayOverridesToRange(
            range,
            editFormData.displayOverrides,
            syncedVisibility,
          )
        : range,
    );

    let nextDto = null;
    const currentList = getPriceRangeListFromDto(dtoDraft, Boolean(useCity));
    if (dtoDraft && Array.isArray(currentList)) {
      nextDto = {
        ...dtoDraft,
        isAgentEdited: true,
        [listKey]: currentList.map((item, index) =>
          index === editingSourceIndex
            ? applyEditorStateToDtoRangeItem(
                item,
                editFormData.displayOverrides,
                syncedVisibility,
              )
            : item,
        ),
      };
    }

    this.setState({
      rangesDraft: nextRanges,
      dtoDraft: nextDto,
      saving: true,
      saveError: null,
      saveSuccess: null,
    });

    const success = await updateDashBoardDetailsWithPriceRange({
      listgenUserID,
      mlsAreaID,
      useCity: Boolean(useCity),
      dashBoardDetailsWithPriceRange: nextRanges,
      priceRangeDashBoardDetailsDTO: nextDto || undefined,
    });

    if (success) {
      this.setState({
        saving: false,
        editFormBaseline: this.cloneEditFormSnapshot(editFormData),
      });
      this.showSaveSuccess("Saved");
      return;
    }

    this.setState({
      rangesDraft: previousRangesDraft,
      dtoDraft: previousDtoDraft,
      saving: false,
      saveError: "Unable to save price range details. Please try again.",
      saveSuccess: null,
    });
  };

  render() {
    const {
      printVersion,
      agentData,
      currentArea,
      showEditButtons,
      useTwoSectionLayout,
    } = this.props;
    const {
      rangesDraft,
      modalOpen,
      editFormData,
      editingSourceIndex,
      saving,
      saveError,
      saveSuccess,
    } = this.state;

    if (!rangesDraft || rangesDraft.length === 0) {
      return <div className="dashboard-data-pending">Data pending</div>;
    }

    const reversed = [...rangesDraft].reverse();
    const visibleRanges = useTwoSectionLayout ? reversed.slice(0, 2) : reversed;
    const mappedRanges = visibleRanges.map((range, i) => {
      const sourceIndex = rangesDraft.length - 1 - i;
      const rangeNum = i + 1;
      return (
        <DashboardBodyRange
          printVersion={printVersion}
          key={`${range.PriceRangeLow}-${range.PriceRangeHigh}-${sourceIndex}`}
          rangeNum={rangeNum}
          baths={range.AverageBaths}
          halfBaths={range.AverageHalfBaths}
          beds={range.AverageBeds}
          dom={range.AverageDaysOnMarket}
          garageSpaces={range.AverageGarageSpaces}
          sqft={range.AverageSquareFeet}
          lotSize={range.AverageLotSize}
          homesSold={range.HomesSold}
          oceanView={range.OceanView}
          golfView={range.golfView}
          priceRangeLow={range.PriceRangeLow}
          priceRangeHigh={range.PriceRangeHigh}
          displayOverrides={range.displayOverrides || null}
          fieldVisibility={range.fieldVisibility || null}
          agentData={agentData}
          currentArea={currentArea}
          showEditButton={showEditButtons}
          isEdited={isRangeDisplayEdited(range)}
          onEditClick={() => this.openEditModal(sourceIndex, rangeNum)}
          editDisabled={saving}
          useTwoSectionLayout={useTwoSectionLayout}
        />
      );
    });

    return (
      <div
        className={`dashboard-body${
          useTwoSectionLayout ? " dashboard-body-two-sections" : ""
        }`}
      >
        {mappedRanges}
        {showEditButtons ? (
          <DashboardBodyRangeEditorModal
            open={modalOpen}
            rangeNum={
              editingSourceIndex != null
                ? rangesDraft.length - editingSourceIndex
                : null
            }
            editFormData={editFormData}
            isDirty={this.isEditFormDirty()}
            onClose={this.requestCloseEditModal}
            onChange={this.handleInputChange}
            onVisibilityChange={this.handleVisibilityChange}
            onRevertField={this.handleRevertField}
            onRevertAllFields={this.handleRevertAllFields}
            onSave={this.onEditFormSubmit}
            saving={saving}
            saveSuccess={Boolean(saveSuccess)}
            saveError={saveError}
          />
        ) : null}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  updateDashBoardDetailsWithPriceRange: (payload) =>
    dispatch(pmaActions.updateDashBoardDetailsWithPriceRange(payload)),
});

export default connect(null, mapDispatchToProps)(DashboardBody);
